import React, { useCallback, useState } from 'react'
import { Group, Image, Rect } from 'react-konva'
import type { UnitState } from '../types'
import { CELL_SIZE, COLORS } from '../constants'
import type { ImageMap } from '../hooks/useImageLoader'

interface UnitProps {
  x: number
  y: number
  state: UnitState
  isEmpty?: boolean
  images?: ImageMap | null
  onMouseDown?: () => void
  onMouseEnter?: () => void
  onMouseUp?: () => void
}

const stateImageKey: Record<UnitState, string> = {
  off: 'unitOff',
  on: 'unitOn',
  select: 'unitSelect',
  done: 'unitDone',
}

const fallbackColorMap: Record<UnitState, string> = {
  off: COLORS.unitOff,
  on: COLORS.unitOn,
  select: COLORS.unitSelect,
  done: '#C8E6C9',
}

const hoverColorMap: Record<UnitState, string> = {
  off: '#D4BC82',
  on: '#FFC85C',
  select: COLORS.unitSelect,
  done: '#C8E6C9',
}

export const Unit: React.FC<UnitProps> = ({
  x,
  y,
  state,
  isEmpty,
  images,
  onMouseDown,
  onMouseEnter,
  onMouseUp,
}) => {
  const isDone = state === 'done'
  const [hovered, setHovered] = useState(false)
  const img = images?.[stateImageKey[state] as keyof ImageMap] ?? null

  const handleMouseEnter = useCallback(() => {
    if (!isDone) setHovered(true)
    onMouseEnter?.()
  }, [isDone, onMouseEnter])

  const handleMouseLeave = useCallback(() => {
    setHovered(false)
  }, [])

  // Empty cells can become active without changing the hook order.
  if (isEmpty) return null

  if (img) {
    return (
      <Group>
        {/* 호버 하이라이트 (이미지 뒤) */}
        {hovered && !isDone && (
          <Rect
            x={x - 2}
            y={y - 2}
            width={CELL_SIZE + 4}
            height={CELL_SIZE + 4}
            stroke="#FFF"
            strokeWidth={2}
            cornerRadius={6}
            listening={false}
            opacity={0.8}
          />
        )}
        <Image
          x={x}
          y={y}
          width={CELL_SIZE}
          height={CELL_SIZE}
          image={img}
          opacity={isDone ? 0.6 : 1}
          listening={!isDone}
          onMouseDown={isDone ? undefined : onMouseDown}
          onTouchStart={isDone ? undefined : onMouseDown}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseUp={isDone ? undefined : onMouseUp}
          onTouchEnd={isDone ? undefined : onMouseUp}
        />
      </Group>
    )
  }

  // Fallback: 이미지 없을 때 색상 사각형
  return (
    <Rect
      x={x}
      y={y}
      width={CELL_SIZE}
      height={CELL_SIZE}
      fill={hovered && !isDone ? hoverColorMap[state] : fallbackColorMap[state]}
      cornerRadius={4}
      stroke={state === 'select' ? COLORS.unitSelect : isDone ? '#A5D6A7' : COLORS.gridBorder}
      strokeWidth={state === 'select' ? 2 : 1}
      opacity={isDone ? 0.7 : 1}
      listening={!isDone}
      onMouseDown={isDone ? undefined : onMouseDown}
      onTouchStart={isDone ? undefined : onMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseUp={isDone ? undefined : onMouseUp}
      onTouchEnd={isDone ? undefined : onMouseUp}
    />
  )
}
